import { useEffect, useState } from "react";
import { api, formatPerWeight, formatDate, WEIGHT_UNITS } from "@/lib/api";
import {
  TrendUp,
  Storefront,
  ChartLineUp,
  Users,
  Package,
  Clock,
  ArrowsClockwise,
  ArrowUpRight,
  ArrowDown,
  Lightning,
  ClipboardText,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import UnitToggle from "@/components/UnitToggle";

const StatCard = ({ label, value, icon: Icon, testid, sub }) => (
  <div className="surface p-5 fade-up relative" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-eyebrow mb-3">{label}</div>
        <div className="text-3xl font-light" style={{ color: "var(--text-primary)" }}>
          {value}
        </div>
        {sub && (
          <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>{sub}</div>
        )}
      </div>
      <div className="p-2" style={{ color: "var(--gold)" }}>
        <Icon size={22} weight="duotone" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { unit } = useSettings();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/dashboard"),
      api.get("/dashboard/alerts"),
      api.get("/briefing/latest").catch(() => ({ data: null })),
    ])
      .then(([r1, r2, r3]) => {
        setData(r1.data);
        setAlerts(r2.data || []);
        setBriefing(r3.data || null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const unitLabel = WEIGHT_UNITS.find((u) => u.value === unit)?.label || "100g";
  const hasBriefing = !!briefing;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Visão Geral</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>
            Inteligência de Mercado
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Acompanhamento de portfólio de referência, comparativos de preço entre canais online e mapeamento de novos players.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <UnitToggle />
          <button
            onClick={load}
            className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
            data-testid="dashboard-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" /> Atualizar
          </button>
        </div>
      </header>

      {/* Diagnosis guidance */}
      {!loading && !hasBriefing && (
        <Link
          to="/diagnostico"
          className="surface p-6 flex flex-col md:flex-row md:items-center gap-4 transition-all hover:shadow-md"
          style={{
            background: "linear-gradient(135deg, rgba(184,148,31,0.06), rgba(15,58,42,0.04))",
            border: "1px solid rgba(184,148,31,0.35)",
            textDecoration: "none",
          }}
          data-testid="diagnosis-cta"
        >
          <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: "rgba(184,148,31,0.12)", border: "1px solid rgba(184,148,31,0.3)" }}>
            <ClipboardText size={24} weight="duotone" style={{ color: "var(--gold)" }} />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>Comece por aqui</div>
            <div className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>
              Preencha o diagnóstico estratégico
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              35 perguntas em 8 seções (identificação, portfólio, clientes, concorrência, preço, digital, equipe, adaptações). Em poucos minutos você recebe matriz SWOT, gráfico de maturidade e próximos passos contextuais — todas as demais abas ficam mais precisas depois disso.
            </p>
          </div>
          <span className="btn-gold px-4 py-2 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-2 shrink-0 self-start md:self-center">
            Iniciar diagnóstico <ArrowRight size={14} weight="bold" />
          </span>
        </Link>
      )}

      {!loading && hasBriefing && (
        <Link
          to="/diagnostico"
          className="surface px-5 py-3 flex items-center gap-3 transition-colors surface-hover"
          style={{ textDecoration: "none" }}
          data-testid="diagnosis-resume"
        >
          <CheckCircle size={18} weight="duotone" style={{ color: "var(--success)" }} />
          <div className="flex-1 min-w-0">
            <span className="text-sm" style={{ color: "var(--text-primary)" }}>
              Diagnóstico estratégico de <strong>{briefing.company_name}</strong>
            </span>
            <span className="text-xs ml-2" style={{ color: "var(--text-secondary)" }}>
              · maturidade {briefing.maturity_scores?.overall ?? "—"}/100
            </span>
          </div>
          <span className="text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>Ver →</span>
        </Link>
      )}


      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Produtos no Portfólio"
          value={loading ? "…" : data?.portfolio_count ?? 0}
          icon={Storefront}
          testid="kpi-portfolio"
          sub="base de referência"
        />
        <StatCard
          label="Produtos Rastreados"
          value={loading ? "…" : data?.tracked_count ?? 0}
          icon={Package}
          testid="kpi-tracked"
          sub="com ofertas online ativas"
        />
        <StatCard
          label={`Preço Médio /${unitLabel}`}
          value={loading ? "…" : formatPerWeight(data?.avg_norm, unit)}
          icon={TrendUp}
          testid="kpi-avgprice"
          sub={`${data?.total_offers ?? 0} ofertas analisadas`}
        />
        <StatCard
          label="Players Detectados"
          value={loading ? "…" : data?.competitors_count ?? 0}
          icon={Users}
          testid="kpi-competitors"
          sub={`em ${data?.channels_count ?? 0} canais`}
        />
      </div>

      {/* Ações urgentes */}
      {alerts.length > 0 && (
        <section className="surface" data-testid="dashboard-alerts">
          <div className="px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-default)" }}>
            <Lightning size={18} weight="duotone" style={{ color: "var(--gold)" }} />
            <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Ações urgentes</h2>
            <span className="text-xs ml-auto" style={{ color: "var(--text-secondary)" }}>
              {alerts.filter((a) => a.level === "opportunity").length} oportunidade(s) · {alerts.filter((a) => a.level === "risk").length} risco(s)
            </span>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border-default)" }}>
            {alerts.map((a, i) => {
              const isOpp = a.level === "opportunity";
              const color = isOpp ? "var(--success)" : "var(--danger)";
              const Icon = isOpp ? ArrowUpRight : ArrowDown;
              return (
                <Link
                  key={i}
                  to={`/rastrear/${a.tracked_id}`}
                  className="px-6 py-4 flex items-start gap-3 border-b last:border-b-0 transition-colors surface-hover"
                  style={{ borderColor: "var(--border-default)", textDecoration: "none" }}
                  data-testid={`alert-${i}`}
                >
                  <span className="mt-0.5" style={{ color }}><Icon size={16} weight="bold" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{a.product_name}</div>
                    <div className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>{a.text}</div>
                  </div>
                  <span className="text-xs font-semibold whitespace-nowrap shrink-0" style={{ color }}>
                    {a.diff_pct > 0 ? "+" : ""}{a.diff_pct.toFixed(0)}%
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="surface" data-testid="recent-tracked">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-default)" }}>
          <div className="flex items-center gap-2">
            <ChartLineUp size={18} weight="duotone" style={{ color: "var(--gold)" }} />
            <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Últimos rastreamentos</h2>
          </div>
          <Link to="/rastrear" className="text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>
            Ver todos →
          </Link>
        </div>

        {loading ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>
        ) : (data?.recent_tracked ?? []).length === 0 ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>
            Nenhum produto rastreado ainda. Vá em <Link to="/rastrear" className="gold-text underline">Rastrear Produto</Link> para começar.
          </div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                  <th className="px-6 py-3 label-eyebrow">Produto</th>
                  <th className="px-6 py-3 label-eyebrow">Ofertas</th>
                  <th className="px-6 py-3 label-eyebrow">Mín. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Méd. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Máx. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Atualizado</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_tracked.map((t) => (
                  <tr key={t.id} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }}>
                    <td className="px-6 py-3">
                      <Link to={`/rastrear/${t.id}`} className="hover:text-[var(--gold)]" style={{ color: "var(--text-primary)" }}>
                        {t.query}
                      </Link>
                    </td>
                    <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>{t.normalized_count ?? 0}/{t.offer_count}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{formatPerWeight(t.min_norm, unit)}</td>
                    <td className="px-6 py-3" style={{ color: "var(--gold)" }}>{formatPerWeight(t.avg_norm, unit)}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{formatPerWeight(t.max_norm, unit)}</td>
                    <td className="px-6 py-3 text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                      <Clock size={12} /> {formatDate(t.last_updated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
